/// <amd-module name="Favorites/Star/FavoritesStar" />
import { Favorite, FavoriteCreateParameters } from "Favorites/Common/Contracts/Favorite";
import { IFavoritesCollection, IFavoritesService } from "Favorites/Common/FavoritesService";
import { FavoritesStarButton } from "Favorites/Star/FavoritesStarButton";
import * as React from "react";
import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { IVssNavigationService } from "VSS/Platform/Navigation";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";

export interface FavoritesStarState {
    favorite: Favorite | undefined;
}

export interface FavoritesStarProps extends IVssComponentProperties {
    /** Identifying information about the favorite */
    artifact: FavoriteCreateParameters;
    buttonClassName?: string;
    serviceInstanceId?: string;
    /**
     * Favorited aria-label
     */
    favoritedArialabel?: string;

    /**
     * UnFavorited aria-label
     */
    unFavoritedArialabel?: string;

    /**
     * Optional callback function for adding favorite
     */
    onAddFavorite?: (event?: React.MouseEvent<any>) => void;

    /**
     * Optional callback function for removing favorite
     */
    onRemoveFavorite?: (event?: React.MouseEvent<any>) => void;
}

export namespace FavoritesCustomerIntelligenceConstants {
    export const AREA = "Favorites";
    export const FAVORITE_TOGGLE_CLICK = "ToggleFavoriteClick";
    export const FAVORITE_DELETE_CLICK = "DeleteFavoriteClick";
}

/**
 * Shared favorite "star" view to display favorite star for any kind of favoritable artifact
 */
export class FavoritesStar extends VssComponent<FavoritesStarProps, FavoritesStarState> {
    public static componentType = "favoritesStar";

    private readonly _favoritesService: IFavoritesService;
    private _isComponentMounted: boolean = false;
    private _telemetryService: IVssTelemetryService;
    private _navigationService: IVssNavigationService;

    constructor(props: FavoritesStarProps, context: IVssComponentContext) {
        super(props, context);
        this.state = { favorite: undefined };

        this._favoritesService = this.context.pageContext.getService("IFavoritesService");
        this._favoritesService.subscribe(this._onFavoriteAdded, "favoriteAdded");
        this._favoritesService.subscribe(this._onFavoriteRemoved, "favoriteRemoved");
    }

    public render(): JSX.Element | null {
        if (!this._favoritesService.canUseFavorites()) {
            return null;
        }

        return (
            <FavoritesStarButton
                buttonClassName={this.props.buttonClassName}
                className={this.props.className}
                favoritedArialabel={this.props.favoritedArialabel}
                unFavoritedArialabel={this.props.unFavoritedArialabel}
                isFavorited={!!this.state.favorite}
                onToggleFavorite={this._onToggleFavorite}
            />
        );
    }

    public componentDidMount(): void {
        this._isComponentMounted = true;
        this._loadFavoritesData();
    }

    public componentDidUpdate(prevProps: FavoritesStarProps): void {
        if (prevProps.artifact.artifactId !== this.props.artifact.artifactId) {
            this._onArtifactChanged();
        }
    }

    public componentWillUnmount(): void {
        this._favoritesService.unsubscribe(this._onFavoriteAdded, "favoriteAdded");
        this._favoritesService.unsubscribe(this._onFavoriteRemoved, "favoriteRemoved");
        this._isComponentMounted = false;
    }

    private _loadFavoritesData(): void {
        this._favoritesService
            .getFavorites(this.props.artifact.artifactType, this.props.artifact.artifactScope.type, this.props.artifact.artifactScope.id)
            .then(this._onFavoritesLoaded);
    }

    private _onFavoritesLoaded = (collection: IFavoritesCollection): void => {
        const favoriteItems: Favorite[] = collection.value;
        const favorite = favoriteItems ? favoriteItems.find((fav: Favorite) => this._favoriteMatchesArtifact(fav)) : undefined;
        if (this._isComponentMounted) {
            this.setState({
                favorite: favorite
            });
        }
    };

    private _onToggleFavorite = (event: React.MouseEvent<any>): void => {
        if (this.state.favorite) {
            if (this.props.onRemoveFavorite) {
                this.props.onRemoveFavorite(event);
            }
            this._favoritesService.removeFavorite(this.state.favorite);
        } else {
            if (this.props.onAddFavorite) {
                this.props.onAddFavorite(event);
            }
            const favorite = {
                artifactId: this.props.artifact.artifactId,
                artifactName: this.props.artifact.artifactName,
                artifactScope: this.props.artifact.artifactScope,
                artifactType: this.props.artifact.artifactType
            } as Favorite;

            this._favoritesService.addFavorite(favorite);
        }

        if (this.state.favorite && this.state.favorite.artifactIsDeleted) {
            this._publish(FavoritesCustomerIntelligenceConstants.FAVORITE_DELETE_CLICK, { favoriteType: this.props.artifact.artifactType });
        } else {
            this._publish(FavoritesCustomerIntelligenceConstants.FAVORITE_TOGGLE_CLICK, {
                favoriteType: this.props.artifact.artifactType,
                favorited: !this.state.favorite
            });
        }
    };

    private _onFavoriteAdded = (favorite: Favorite): void => {
        if (this._favoriteMatchesArtifact(favorite)) {
            this.setState({
                favorite: favorite
            });
        }
    };

    private _onFavoriteRemoved = (favorite: Favorite): void => {
        if (this._favoriteMatchesArtifact(favorite)) {
            this.setState({
                favorite: undefined
            });
        }
    };

    private _onArtifactChanged = (): void => {
        this._loadFavoritesData();
    }

    // Different hubs (Boards, Backlogs, Sprints, etc) had the same artifactId guid and need artifactType in addition to differentiate them
    private _favoriteMatchesArtifact = (favorite: Favorite): boolean => {
        const artifact: FavoriteCreateParameters = this.props.artifact;
        return favorite.artifactId === artifact.artifactId && favorite.artifactType === artifact.artifactType;
    };

    private _publish(featureName: string, ciData: { [key: string]: any } = {}): void {
        this._navigationService = this.context.pageContext.getService<IVssNavigationService>("IVssNavigationService");
        ciData["routeId"] = this._navigationService.getCurrentRoute().routeId;
        this._telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        this._telemetryService.publishEvent(FavoritesCustomerIntelligenceConstants.AREA, featureName, ciData);
    }
}

VssComponent.register(FavoritesStar.componentType, FavoritesStar);
